import { isIdShaped } from 'context/workflowReference';
import {
  readInvocationTarget, scheduleDescription
} from 'blob_types/blobhub/compute/scheduler/schedules/invocationTarget';

export const WORKFLOW_IN_TARGET = "target";
export const WORKFLOW_FROM_BLOB = "blob";

export const ID_UNDER_DEFAULT = "This workflow follows the default revision, so name it by alias. A new revision " +
  "gives every workflow a new ID, and the schedule would stop firing.";

// The platform caps every part of a target and a workflow at this length.
const PART_MAX_LENGTH = 64;

// Each field as its label names it.
const PART_NAMES = {
  org: "organization", blob: "blob", revision: "revision", session: "session", definition: "workflow",
  sourceOrg: "organization", sourceBlob: "blob", sourceRevision: "revision", sourceDefinition: "workflow"
};

const INPUT_DATA_NOT_ARRAY = "Input data must be a JSON array, such as [].";

function aliasNeedsOrg(blob) {
  return `Add the organization. ${blob} is an alias, and an alias names a blob only within one organization.`;
}

function partTooLong(field) {
  return `Keep the ${PART_NAMES[field]} to ${PART_MAX_LENGTH} characters.`;
}

function refuse(field, message) {
  return { field: field, message: message };
}

function tooLong(parts) {
  const field = Object.keys(parts).find((key) => parts[key].length > PART_MAX_LENGTH);
  return field ? refuse(field, partTooLong(field)) : null;
}

export function organizationHint(blob) {
  return !blob.trim() || isIdShaped(blob) ? "(optional)" : "(required)";
}

export function targetWorkflowHelp(blob, revision) {
  if ( "default" === revision.trim() ) {
    return `A workflow in ${blob.trim() || "the target"}'s default revision, by alias. Pin the revision above to ` +
      "name one by ID.";
  }
  return "A workflow in the pinned revision above, by alias or ID.";
}

export function checkTarget({ org, blob, revision, session }) {
  const target = {};
  if ( org.trim() ) {
    target.org = org.trim();
  }
  target.blob = blob.trim();
  target.revision = revision.trim();
  target.session = session.trim();
  const overlong = tooLong({ org: org.trim(), blob: target.blob, revision: target.revision, session: target.session });
  if ( overlong ) {
    return { target: target, error: overlong };
  }
  let error = null;
  if ( !target.blob ) {
    error = refuse("blob", "Name the blob, by ID or alias.");
  } else if ( !target.org && !isIdShaped(target.blob) ) {
    error = refuse("org", aliasNeedsOrg(target.blob));
  } else if ( !target.revision ) {
    error = refuse("revision", "Name a revision ID, or default.");
  } else if ( !target.session ) {
    error = refuse("session", "Name the session the schedule runs in.");
  } else if ( "default" === target.revision && isIdShaped(target.session) ) {
    error = refuse("session", "A target that follows the default revision names its session by alias.");
  }
  return { target: target, error: error };
}

// `targetRevision` decides only for the `{definition}` form. A workflow from another blob names its own
// revision, which the platform's resolver compares with `default` case-insensitively.
export function checkWorkflow({ mode, definition, source }, targetRevision) {
  if ( WORKFLOW_IN_TARGET === mode ) {
    const workflow = { definition: definition.trim() };
    const overlong = tooLong({ definition: workflow.definition });
    if ( overlong ) {
      return { workflow: workflow, error: overlong };
    }
    let error = null;
    if ( !workflow.definition ) {
      error = refuse("definition", "Choose a workflow.");
    } else if ( "default" === targetRevision.trim() && isIdShaped(workflow.definition) ) {
      error = refuse("definition", ID_UNDER_DEFAULT);
    }
    return { workflow: workflow, error: error };
  }
  const workflow = {};
  if ( source.org.trim() ) {
    workflow.org = source.org.trim();
  }
  workflow.blob = source.blob.trim();
  workflow.revision = source.revision.trim();
  workflow.definition = source.definition.trim();
  const overlong = tooLong({
    sourceOrg: source.org.trim(), sourceBlob: workflow.blob,
    sourceRevision: workflow.revision, sourceDefinition: workflow.definition
  });
  if ( overlong ) {
    return { workflow: workflow, error: overlong };
  }
  let error = null;
  if ( !workflow.blob ) {
    error = refuse("sourceBlob", "Name the blob the workflow comes from.");
  } else if ( !workflow.org && !isIdShaped(workflow.blob) ) {
    error = refuse("sourceOrg", aliasNeedsOrg(workflow.blob));
  } else if ( !workflow.revision ) {
    error = refuse("sourceRevision", "Name a revision ID, or default.");
  } else if ( !workflow.definition ) {
    error = refuse("sourceDefinition", "Name the workflow.");
  } else if ( "default" === workflow.revision.toLowerCase() && isIdShaped(workflow.definition) ) {
    error = refuse("sourceDefinition", ID_UNDER_DEFAULT);
  }
  return { workflow: workflow, error: error };
}

function parseJson(text) {
  try {
    return JSON.parse(text);
  } catch ( error ) {
    return undefined;
  }
}

export function checkInputData(text) {
  const value = parseJson(text.trim() || "[]");
  if ( Array.isArray(value) ) {
    return { inputData: value, error: null };
  }
  return { inputData: null, error: INPUT_DATA_NOT_ARRAY };
}

export function readScheduleForm(schedule) {
  const target = readInvocationTarget(schedule.invocation_target);
  const workflow = target.workflow || {};
  const fromBlob = !!workflow.blob;
  return {
    org: target.org || "",
    blob: target.blob || "",
    revision: target.revision || "",
    session: target.session || "",
    workflowMode: fromBlob ? WORKFLOW_FROM_BLOB : WORKFLOW_IN_TARGET,
    workflowDefinition: fromBlob ? "" : workflow.definition || "",
    sourceOrg: fromBlob ? workflow.org || "" : "",
    sourceBlob: fromBlob ? workflow.blob : "",
    sourceRevision: fromBlob ? workflow.revision || "" : "default",
    sourceDefinition: fromBlob ? workflow.definition || "" : "",
    description: scheduleDescription(schedule) || "",
    inputData: JSON.stringify(target.inputData || [], null, 2)
  };
}
